import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { listenForAuthChanges, logout } from "../../redux/authSlice";
import type { RootState, AppDispatch } from "../../redux/store";

/* merchantSlice is ONLY for identity + ownership: who am I, which merchants
   are mine, which one is selected. accountingSlice is gone — this dashboard
   no longer reads or displays any payment/balance data at all. */
import {
  Merchant,
  listenToUserMerchants,
  setCurrentMerchant,
} from "../../redux/merchantSlice";

import {
  Home, Settings, ChevronLeft, ChevronRight, Search,
  LogOut, Key, Webhook, Palette, UserPlus, Store, FlaskConical, BadgeCheck,
  LifeBuoy, Grid3X3, UserCog, Sparkles,
} from "lucide-react";

import logo from "../../assets/app/logo.png";

/* ═══ THE ONLY PAGE IMPORTS ═══
   src/APPCENTRAL/B2B/Webhooks.tsx
   src/APPCENTRAL/B2B/API.tsx
   src/APPCENTRAL/BUSSINESS/CUSTOMERSERVICE/CustomerSupport.tsx
   (this file sits in src/APPCENTRAL/B2B/, so Support resolves one level up) */
import Webhooks from "./Webhooks";
import API from "./API";
import CustomerSupport from "../BUSSINESS/CUSTOMERSERVICE/CustomerSupport";

/* Those three aren't all typed with a merchantId prop yet, and TS rejects an
   unknown prop outright on a component declared with none. One cast keeps this
   file compiling while they catch up — the prop is ignored by whoever doesn't
   read it. */
type PageProps = { merchantId: string };
const asPage = (C: any) => C as React.ComponentType<PageProps>;
const P_Webhooks = asPage(Webhooks);
const P_API = asPage(API);
const P_Support = asPage(CustomerSupport);

/* ─── THEME — no stylesheet anywhere on this page ────────────────────────
   Same idea as API.tsx: no `<style>` block, no className. Every element
   carries its own `style={{...}}`, and the handful of pieces that need a
   hover/press state (beads, buttons, nav items, chips, tabs) are small
   local components below that track that state with useState and
   recompute their own inline style. Colors are unchanged from the
   previous white/3D pass. ─────────────────────────────────────────────── */

const BLACK = "#14141F";
const WHITE = "#FFFFFF";

const BG            = "#F3F4FA";
const SURFACE       = "#FFFFFF";
const SURFACE_SOFT  = "#EEEFF9";
const SURFACE_LIFT  = "#E3E5F3";
const BORDER        = "rgba(20,20,43,.08)";
const BORDER_STRONG = "rgba(20,20,43,.14)";
const SHADOW_INK    = "rgba(20,20,43,.10)";
const TEXT          = "#181829";
const TEXT_DIM      = "#6B6E89";
const TEXT_FAINT    = "#9DA0BC";

const BLUE        = "#4C6FEE";
const BLUE_DEEP   = "#3350C4";
const VIOLET      = "#8B5BE8";
const VIOLET_DEEP = "#6B3FC0";
const PINK        = "#E93E6D";
const PINK_DEEP   = "#C22753";
const ORANGE_DEEP = "#CE6608";
const YELLOW      = "#E8C51E";
const YELLOW_DEEP = "#B99A05";
const GREEN       = "#3FBE55";
const GREEN_DEEP  = "#2C9540";

const WORDMARK      = `linear-gradient(90deg, ${BLUE} 0%, ${VIOLET} 18%, ${PINK} 38%, ${"#F3821E"} 60%, ${YELLOW} 80%, ${GREEN} 100%)`;
const WORDMARK_DIAG = `linear-gradient(148deg, ${BLUE} 0%, ${VIOLET} 24%, ${PINK} 50%, ${"#F3821E"} 76%, ${YELLOW} 100%)`;

const ACCENT_GLOW = "rgba(233,62,109,.22)";
const ACCENT_SOFT = "rgba(233,62,109,.12)";

const BEVEL_SM = "inset 0 1.5px 0 rgba(255,255,255,.85), inset 0 -1.5px 0 rgba(20,20,43,.05)";

const FONT_LINK =
  "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap";

const DOT = "\u00b7";
const SIDEBAR_W = 252;
const SIDEBAR_W_COLLAPSED = 72;
const HEADER_H = 62;

type NavItem = { id: string; label: string; icon: any; hint: string };

/* Three real imported pages + Home. No accordions — there's nothing left to
   nest, and a disclosure arrow that opens one child is a lie about the shape
   of the product. Activity is gone along with everything payment-related. */
const NAV: NavItem[] = [
  { id: "home",     label: "Home",     icon: Home,    hint: "Today at a glance" },
  { id: "api",      label: "API",      icon: Key,     hint: "Keys and endpoints" },
  { id: "webhooks", label: "Webhooks", icon: Webhook, hint: "Event delivery" },
  { id: "support",  label: "Support",  icon: LifeBuoy, hint: "Talk to a human" },
];

const titles: Record<string, string> = {
  home: "Dashboard",
  api: "API",
  webhooks: "Webhooks",
  support: "Support",
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/* ═══════════════════════════════════════════════════════════════════════
   STYLE HELPERS — replace the .eb-* class rules that don't need state.
   ═══════════════════════════════════════════════════════════════════════ */

const mono = (extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'IBM Plex Mono',ui-monospace,monospace",
  fontWeight: 600, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase",
  ...extra,
});

const heading = (size: number | string, extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'Fredoka',system-ui,sans-serif", fontWeight: 600, margin: 0,
  letterSpacing: "-.03em", lineHeight: 1.05, fontSize: size, ...extra,
});

const trunc: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const plateStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  position: "relative", background: SURFACE, borderRadius: 26, padding: 20, overflow: "hidden",
  boxShadow: `${BEVEL_SM}, 0 8px 0 ${SURFACE_LIFT}, 0 8px 20px -14px ${SHADOW_INK}`,
  ...extra,
});

const rootStyle: React.CSSProperties = {
  position: "relative", fontFamily: "'Space Grotesk',system-ui,sans-serif", fontWeight: 500,
  color: TEXT, background: BG, WebkitFontSmoothing: "antialiased",
} as React.CSSProperties;

const lightWashStyle: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
  background:
    `radial-gradient(38% 30% at 4% 0%, rgba(76,111,238,.10) 0%, transparent 66%),` +
    `radial-gradient(32% 26% at 98% 14%, rgba(233,62,109,.08) 0%, transparent 64%),` +
    `radial-gradient(36% 28% at 60% 100%, rgba(63,190,85,.06) 0%, transparent 64%)`,
};

const centerWrapStyle: React.CSSProperties = {
  minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative",
};

const dropdownStyle = (width: number, right: number | string): React.CSSProperties => ({
  position: "absolute", top: "calc(100% + 10px)", background: SURFACE, borderRadius: 22, overflow: "hidden", zIndex: 200,
  boxShadow: `${BEVEL_SM}, 0 6px 0 ${SURFACE_LIFT}, 0 30px 60px -24px ${SHADOW_INK}`,
  width, right,
});

const dividerStyle: React.CSSProperties = { height: 1, background: BORDER, margin: "4px 0" };

const switcherPopStyle: React.CSSProperties = {
  position: "absolute", left: 12, right: 12, bottom: "calc(100% + 8px)", zIndex: 150, background: SURFACE,
  borderRadius: 18, overflow: "hidden", maxHeight: 220, overflowY: "auto",
  boxShadow: `${BEVEL_SM}, 0 6px 0 ${SURFACE_LIFT}, 0 24px 50px -20px ${SHADOW_INK}`,
};

const heroSlabStyle: React.CSSProperties = {
  position: "relative", borderRadius: 30, padding: "clamp(22px,3.4vw,32px)", overflow: "hidden",
  background: WORDMARK_DIAG, color: WHITE,
  boxShadow: `inset 0 1.5px 0 rgba(255,255,255,.3), 0 10px 0 ${VIOLET_DEEP}, 0 24px 50px -26px ${ACCENT_GLOW}`,
};

/* ═══════════════════════════════════════════════════════════════════════
   LOCAL PRESENTATIONAL COMPONENTS
   ═══════════════════════════════════════════════════════════════════════ */

function Spinner({ size = 40 }: { size?: number }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      style={{
        width: size, height: size, borderRadius: "50%",
        background: `conic-gradient(from 0deg, transparent 0deg, ${BLUE} 90deg, ${PINK} 180deg, ${YELLOW} 270deg, transparent 360deg)`,
        WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0)",
        mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0)",
      } as React.CSSProperties}
      animate={reduceMotion ? { rotate: 0 } : { rotate: 360 }}
      transition={reduceMotion ? { duration: 0 } : { repeat: Infinity, ease: "linear", duration: 0.9 }}
    />
  );
}

/* Flat color circle, glossy top highlight, solid colored offset shadow.
   Renders as a plain <span> when there's no onClick (decorative icon),
   or a real <button> with hover-lift / press-flat when there is. */
function Bead({
  children, size = 46, tone = "blue", onClick, ariaLabel, title, style,
}: {
  children: React.ReactNode; size?: number; tone?: "blue" | "violet" | "pink" | "glass" | "mark";
  onClick?: () => void; ariaLabel?: string; title?: string; style?: React.CSSProperties;
}) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const glossy = (c: string) =>
    `radial-gradient(circle at 34% 26%, rgba(255,255,255,.9) 0%, rgba(255,255,255,.12) 42%, transparent 60%), ${c}`;

  const TONES: Record<string, { bg: string; color: string; offset: string }> = {
    blue:   { bg: glossy(BLUE), color: WHITE, offset: BLUE_DEEP },
    violet: { bg: glossy(VIOLET), color: WHITE, offset: VIOLET_DEEP },
    pink:   { bg: glossy(PINK), color: WHITE, offset: PINK_DEEP },
    glass:  { bg: `radial-gradient(circle at 34% 26%, #fff 0%, ${SURFACE_SOFT} 60%), ${SURFACE}`, color: hover ? TEXT : TEXT_DIM, offset: BORDER_STRONG },
    mark:   { bg: WORDMARK_DIAG, color: WHITE, offset: VIOLET_DEEP },
  };
  const t = TONES[tone];

  const lift = !onClick ? 0 : active ? 3 : hover ? -3 : 0;
  const shadowY = !onClick ? 6 : active ? 1 : hover ? 9 : 6;

  const base: React.CSSProperties = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: t.bg, color: t.color,
    boxShadow: `${BEVEL_SM}, 0 ${shadowY}px 0 ${t.offset}`,
    border: "none",
    transform: `translateY(${lift}px)`,
    transition: reduceMotion ? "none" : "transform .2s cubic-bezier(.2,.9,.3,1.5), box-shadow .2s, color .15s",
    ...style,
  };

  if (!onClick) return <span style={base}>{children}</span>;

  return (
    <button
      type="button" onClick={onClick} aria-label={ariaLabel} title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{ ...base, cursor: "pointer" }}
    >
      {children}
    </button>
  );
}

function Btn({
  children, onClick, variant = "primary", style,
}: {
  children: React.ReactNode; onClick?: () => void; variant?: "primary" | "ghost"; style?: React.CSSProperties;
}) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const bg = variant === "primary" ? WORDMARK : (hover ? SURFACE : SURFACE_SOFT);
  const color = variant === "primary" ? WHITE : TEXT;
  const shadow = variant === "primary"
    ? `inset 0 1.5px 0 rgba(255,255,255,.4), 0 ${active ? 1 : hover ? 9 : 6}px 0 ${BLUE_DEEP}`
    : `inset 0 0 0 1.5px ${BORDER_STRONG}`;
  const lift = variant === "primary" ? (active ? 3 : hover ? -3 : 0) : (hover ? -2 : 0);

  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9, whiteSpace: "nowrap",
        fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 14.5, padding: "13px 22px", borderRadius: 999,
        background: bg, color, boxShadow: shadow, border: "none", cursor: "pointer",
        textShadow: variant === "primary" ? "0 1px 2px rgba(0,0,0,.2)" : "none",
        transform: `translateY(${lift}px)`,
        transition: reduceMotion ? "none" : "transform .18s cubic-bezier(.2,.9,.3,1.5), box-shadow .18s, background .15s",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function CollapseToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      aria-label={open ? "Collapse sidebar" : "Expand sidebar"} title={open ? "Collapse sidebar" : "Expand sidebar"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 10, flexShrink: 0,
        background: hover ? SURFACE_LIFT : SURFACE_SOFT, color: hover ? TEXT : TEXT_DIM,
        border: "none", cursor: "pointer",
        transform: active ? "scale(.92)" : "scale(1)",
        transition: "background .15s, color .15s, transform .1s",
      }}
    >
      {open ? <ChevronLeft size={15} strokeWidth={2.6} /> : <ChevronRight size={15} strokeWidth={2.6} />}
    </button>
  );
}

function NavButton({
  icon: Icon, label, hint, active, collapsed, onClick,
}: { icon: any; label: string; hint: string; active: boolean; collapsed: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick} title={collapsed ? label : hint}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: "100%", padding: "9px 10px", borderRadius: 16, display: "flex", alignItems: "center", gap: 10,
        marginBottom: 3, background: active || hover ? SURFACE_SOFT : "transparent",
        boxShadow: active ? `inset 0 0 0 1px ${BORDER_STRONG}` : "none",
        border: "none", cursor: "pointer", textAlign: "left",
        justifyContent: collapsed ? "center" : "flex-start",
        transition: "background .15s",
      }}
    >
      <span style={{
        width: 30, height: 30, borderRadius: 11, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: active ? BLUE : SURFACE_SOFT, color: active ? WHITE : TEXT_DIM,
        boxShadow: active ? `${BEVEL_SM}, 0 3px 0 ${BLUE_DEEP}` : `inset 0 0 0 1px ${BORDER}`,
        transition: "background .15s, color .15s, box-shadow .15s",
      }}>
        <Icon size={15} strokeWidth={2.4} />
      </span>
      {!collapsed && (
        <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <span style={{ display: "block", fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 13, color: TEXT }}>{label}</span>
          <span style={{ display: "block", fontSize: 10.5, color: TEXT_FAINT, marginTop: 1, ...trunc }}>{hint}</span>
        </span>
      )}
    </button>
  );
}

function MenuButton({
  icon: Icon, leading, children, onClick, danger, active,
}: { icon?: any; leading?: React.ReactNode; children: React.ReactNode; onClick?: () => void; danger?: boolean; active?: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: "100%", padding: "11px 16px", textAlign: "left", fontSize: 12.5,
        color: danger ? PINK_DEEP : TEXT, fontWeight: 600, display: "flex", alignItems: "center", gap: 9,
        background: active ? SURFACE_LIFT : hover ? SURFACE_SOFT : "transparent",
        border: "none", cursor: "pointer", transition: "background .12s",
      }}
    >
      {leading}
      {Icon && <Icon size={14} strokeWidth={2.2} style={{ color: danger ? PINK_DEEP : TEXT_DIM }} />}
      {children}
    </button>
  );
}

function SwitcherItem({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", textAlign: "left",
        background: active || hover ? SURFACE_SOFT : "transparent",
        boxShadow: active ? `inset 0 0 0 1px ${BORDER_STRONG}` : "none",
        border: "none", cursor: "pointer", transition: "background .12s",
      }}
    >
      <Bead size={30} tone="mark">{label.charAt(0).toUpperCase()}</Bead>
      <span style={{ ...trunc, fontSize: 12.5, fontWeight: 600 }}>{label}</span>
    </button>
  );
}

function GridQuickButton({ icon: Icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        padding: "12px 6px", borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
        color: hover ? TEXT : TEXT_DIM, background: hover ? SURFACE_SOFT : "transparent",
        border: "none", cursor: "pointer", transition: "background .15s, color .15s",
      }}
    >
      <Icon size={16} strokeWidth={2.2} />
      <span style={{ fontSize: 10, fontWeight: 600 }}>{label}</span>
    </button>
  );
}

function ModeChip({ isLive, onClick }: { isLive: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const clickable = !isLive;
  const bg = isLive
    ? `radial-gradient(circle at 34% 26%, #fff 0%, ${GREEN} 60%)`
    : `radial-gradient(circle at 34% 26%, #fff 0%, ${YELLOW} 60%)`;
  const color = isLive ? WHITE : BLACK;
  const offset = isLive ? GREEN_DEEP : YELLOW_DEEP;
  const lift = clickable && hover ? -2 : 0;

  return (
    <button
      type="button" onClick={clickable ? onClick : undefined} disabled={!clickable}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title={isLive ? "This account is taking real payments" : "You're in test mode"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "5px 12px", borderRadius: 999,
        fontFamily: "'Fredoka',sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase",
        background: bg, color, border: "none", cursor: clickable ? "pointer" : "default",
        boxShadow: `${BEVEL_SM}, 0 ${clickable && hover ? 6 : 4}px 0 ${offset}`,
        transform: `translateY(${lift}px)`,
        transition: "transform .16s cubic-bezier(.2,.9,.3,1.5), box-shadow .16s",
      }}
    >
      {isLive ? <BadgeCheck size={11} strokeWidth={2.8} /> : <FlaskConical size={11} strokeWidth={2.8} />}
      {isLive ? "Live" : "Test"}
    </button>
  );
}

function ModeSegment({
  active, disabled, onClick, color, icon: Icon, label,
}: { active: boolean; disabled?: boolean; onClick?: () => void; color: string; icon: any; label: string }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={disabled ? undefined : onClick} disabled={disabled} aria-pressed={active}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 10px", borderRadius: 999,
        fontFamily: "'Fredoka',sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: ".02em",
        color: active ? color : (hover && !disabled ? TEXT : TEXT_FAINT),
        background: active ? SURFACE : "transparent",
        boxShadow: active ? `${BEVEL_SM}, 0 2px 0 ${BORDER_STRONG}` : "none",
        border: "none", cursor: disabled ? "default" : "pointer", opacity: disabled && !active ? 0.55 : 1,
        transition: "background .16s, color .16s, box-shadow .16s",
      }}
    >
      <Icon size={12} strokeWidth={2.6} />{label}
    </button>
  );
}

/* Oval Test/Live indicator. "Test" is clickable when the current merchant
   is live (jumps to that merchant's test-mode route); "Live" is always
   disabled since clicking it while live has nowhere to go. */
function ModeToggle({ isLive, onSwitchToTest }: { isLive: boolean; onSwitchToTest: () => void }) {
  return (
    <div role="group" aria-label="Account mode"
      style={{ display: "flex", padding: 4, borderRadius: 999, background: SURFACE_SOFT, boxShadow: "inset 0 2px 5px rgba(20,20,43,.08)", width: "100%" }}>
      <ModeSegment active={!isLive} disabled={!isLive} onClick={isLive ? onSwitchToTest : undefined} color={ORANGE_DEEP} icon={FlaskConical} label="Test" />
      <ModeSegment active={isLive} disabled color={GREEN_DEEP} icon={BadgeCheck} label="Live" />
    </div>
  );
}

function Tab({ icon: Icon, label, active, onClick }: { icon: any; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        position: "relative", flex: "1 0 20vw", maxWidth: 120, minWidth: 64,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 4px 12px",
        fontSize: 9.5, fontWeight: 600, color: active ? TEXT : TEXT_FAINT, whiteSpace: "nowrap",
        background: "transparent", border: "none", cursor: "pointer",
      }}
    >
      <Icon size={19} strokeWidth={2.4} />
      <span>{label}</span>
      {active && (
        <motion.span layoutId="eb-tab-underline"
          style={{ position: "absolute", left: "22%", right: "22%", bottom: 4, height: 3, borderRadius: 3, background: WORDMARK }} />
      )}
    </button>
  );
}

function LinkCard({
  icon: Icon, title, description, tone, delay, onClick,
}: { icon: any; title: string; description: string; tone: "blue" | "violet" | "pink"; delay: number; onClick: () => void }) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const toneColor = tone === "blue" ? BLUE_DEEP : tone === "violet" ? VIOLET_DEEP : PINK_DEEP;

  return (
    <motion.button
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.28 }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={plateStyle({
        textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, width: "100%",
        cursor: "pointer", border: "none",
        transform: hover ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hover
          ? `${BEVEL_SM}, 0 12px 0 ${SURFACE_LIFT}, 0 16px 30px -16px ${SHADOW_INK}`
          : `${BEVEL_SM}, 0 8px 0 ${SURFACE_LIFT}, 0 8px 20px -14px ${SHADOW_INK}`,
        transition: reduceMotion ? "none" : "transform .2s cubic-bezier(.2,.9,.3,1.5), box-shadow .2s",
      })}
    >
      <Bead size={46} tone={tone}><Icon size={19} strokeWidth={2.4} /></Bead>
      <span style={{ fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 16, color: TEXT }}>{title}</span>
      <span style={{ fontSize: 12.5, color: TEXT_DIM, lineHeight: 1.6 }}>{description}</span>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4, marginTop: "auto", paddingTop: 4,
        fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
        color: toneColor,
      }}>
        Open <ChevronRight size={13} strokeWidth={2.8} />
      </span>
    </motion.button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */

export default function MerchantDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  /* ═══ STRICT PARAM: merchantId only — no userId fallback, no
     location.state hack. Mirrors StoreCentral's { brandId } read. ═══ */
  const { merchantId } = useParams<{ merchantId: string }>();

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoading = useSelector((s: RootState) => s.auth.status === "loading");
  const authLoaded = !authLoading;
  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);

  /* ── identity + ownership: merchantSlice only, no accounting slice ── */
  const currentMerchant = useSelector((s: RootState) => s.merchant.currentMerchant);
  const mLoading = useSelector((s: RootState) => s.merchant.loading);
  const mError = useSelector((s: RootState) => s.merchant.error);
  const merchants = useSelector((s: RootState) => (s.merchant as any).merchants ?? []) as Merchant[];
  const merchantsLoaded = useSelector((s: RootState) => (s.merchant as any).merchantsLoaded ?? false);
  const merchant = currentMerchant;

  const ownsMerchant = !merchantId || merchants.some((m: Merchant) => m.merchantId === merchantId);

  /* ═══ MODE — a merchant only counts as live when it has BOTH mode:"live"
     AND liveEnabled:true, so mode alone would report live for an account
     that can't actually charge yet. ═══ */
  const isLive = useMemo(() => {
    const m = merchant as any;
    return !!m && m.mode === "live" && m.liveEnabled === true;
  }, [merchant]);

  const [view, setView] = useState("home");
  const [grid, setGrid] = useState(false);
  const [settings, setSettings] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 1024 : false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const popRef = useRef<HTMLDivElement>(null);
  const switcherRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setGrid(false); setSettings(false);
      }
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setGrid(false); setSettings(false); setSwitcherOpen(false); }
    };
    document.addEventListener("mousedown", fn);
    window.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fn);
      window.removeEventListener("keydown", esc);
    };
  }, []);

  /* The content pane scrolls, not the window — a view change has to reset its
     scrollTop or you land halfway down the new page. */
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [view]);

  useEffect(() => {
    if (authLoaded && !authUser?.userId) navigate("/AuthSwitch", { replace: true });
  }, [authLoaded, authUser?.userId, navigate]);

  useEffect(() => {
    if (!authUser?.userId) return;
    const unsub = dispatch(listenToUserMerchants(authUser.userId) as any) as unknown;
    return () => { if (typeof unsub === "function") (unsub as () => void)(); };
  }, [authUser?.userId, dispatch]);

  useEffect(() => {
    if (!merchantId || !merchantsLoaded) return;
    const mine = merchants.find((m: Merchant) => m.merchantId === merchantId);
    dispatch(setCurrentMerchant(mine ?? null));
  }, [merchantId, merchants, merchantsLoaded, dispatch]);

  useEffect(() => {
    if (!merchantId && merchants.length > 0) {
      navigate(`/merchant/${merchants[0].merchantId}`, { replace: true });
    }
  }, [merchantId, merchants, navigate]);

  const go = useCallback((id: string) => {
    setView(id);
    setGrid(false); setSettings(false);
  }, []);

  /* ═══ SETTINGS MENU — API keys used to route away to OrganizationOverview.
     API is a page inside this shell now, so that's a tab switch, not a
     navigation. Same reasoning that already kept Webhooks in-shell. ═══ */
  const settingsItems = useMemo(() => ([
    { key: "account",  label: "Account",  icon: UserCog,  run: () => navigate(`/merchant/${merchantId}/settings`) },
    { key: "team",     label: "Team",     icon: UserPlus, run: () => navigate(`/merchant/${merchantId}/team`) },
    { key: "keys",     label: "API keys", icon: Key,      run: () => setView("api") },
    { key: "webhooks", label: "Webhooks", icon: Webhook,  run: () => setView("webhooks") },
    /* Branding is collected during onboarding — reopen it for this merchant
       rather than duplicating the form. */
    { key: "branding", label: "Branding", icon: Palette,  run: () => navigate(`/merchant/onboarding/${merchantId}`) },
  ]), [navigate, merchantId]);

  /* Test → jump to the dedicated test-mode route for this merchant. Live →
     there's nowhere to send them, they're already looking at it — no-op.
     NOTE: adjust the `/test` path below if your test-mode route differs. */
  const onModeClick = useCallback(() => {
    setSettings(false);
    if (!isLive) navigate(`/merchant/${merchantId}/test`);
  }, [isLive, navigate, merchantId]);

  /* Business switcher — click just swaps the merchantId param. If the
     target merchant is in test mode we send it to the test-mode route
     instead of this (live) dashboard shell. */
  const switchMerchant = useCallback((m: Merchant) => {
    setSwitcherOpen(false);
    setSettings(false);
    if (m.merchantId === merchantId) return;
    const mm = m as any;
    const targetIsLive = mm.mode === "live" && mm.liveEnabled === true;
    navigate(targetIsLive ? `/merchant/${m.merchantId}` : `/merchant/${m.merchantId}/test`);
  }, [navigate, merchantId]);

  const renderPage = () => {
    const id = merchantId!;
    switch (view) {
      case "api":      return <P_API merchantId={id} />;
      case "webhooks": return <P_Webhooks merchantId={id} />;
      case "support":  return <P_Support merchantId={id} />;
      default:
        return <HomePage merchant={merchant} isLive={isLive} onGo={go} />;
    }
  };

  if (!authLoaded || (authUser?.userId && !merchantsLoaded)) {
    return (
      <div style={rootStyle}>
        <link rel="stylesheet" href={FONT_LINK} />
        <div style={lightWashStyle} />
        <div style={centerWrapStyle}>
          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Spinner size={40} />
            <div style={mono({ color: TEXT_DIM, marginTop: 18 })}>Checking your access…</div>
          </div>
        </div>
      </div>
    );
  }

  if (merchantId && !ownsMerchant) {
    return (
      <div style={rootStyle}>
        <link rel="stylesheet" href={FONT_LINK} />
        <div style={lightWashStyle} />
        <div style={centerWrapStyle}>
          <div style={plateStyle({ textAlign: "center", maxWidth: 380, position: "relative", zIndex: 1 })}>
            <Bead size={60} tone="blue" style={{ margin: "0 auto 16px" }}><Store size={22} strokeWidth={2.4} /></Bead>
            <h3 style={heading(19, { marginBottom: 10 })}>This merchant isn't yours</h3>
            <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 22, lineHeight: 1.6 }}>
              The dashboard only opens accounts on your own login. Check you're signed in with the right one.
            </p>
            <Btn variant="primary" onClick={() => navigate(merchants.length ? `/merchant/${merchants[0].merchantId}` : "/merchant/onboarding")}>
              {merchants.length ? "Go to my merchants" : "Create a merchant account"}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  if (mLoading && !merchant) {
    return (
      <div style={rootStyle}>
        <link rel="stylesheet" href={FONT_LINK} />
        <div style={lightWashStyle} />
        <div style={centerWrapStyle}>
          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Spinner size={40} />
            <div style={mono({ color: TEXT_DIM, marginTop: 18 })}>Loading dashboard…</div>
          </div>
        </div>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div style={rootStyle}>
        <link rel="stylesheet" href={FONT_LINK} />
        <div style={lightWashStyle} />
        <div style={centerWrapStyle}>
          <div style={plateStyle({ textAlign: "center", maxWidth: 380, position: "relative", zIndex: 1 })}>
            <Bead size={60} tone="blue" style={{ margin: "0 auto 16px" }}><Sparkles size={22} strokeWidth={2.4} /></Bead>
            <h3 style={heading(19, { marginBottom: 10 })}>No merchant account yet</h3>
            <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 22, lineHeight: 1.6 }}>
              Register a business and this dashboard fills itself in.
            </p>
            <Btn variant="primary" onClick={() => navigate("/merchant/onboarding")}>Set up your merchant account</Btn>
          </div>
        </div>
      </div>
    );
  }

  const initial =
    merchant?.businessName?.charAt(0)?.toUpperCase() ||
    authUser?.email?.charAt(0)?.toUpperCase() || "E";

  return (
    /* ═══ FIXED SHELL — the page itself never scrolls. Sidebar and header are
       pinned; only <main> moves, so imported pages scroll underneath the
       header instead of pushing it off-screen. ═══ */
    <div style={{ ...rootStyle, display: "flex", height: "100vh", maxHeight: "100vh", overflow: "hidden" }}>
      <link rel="stylesheet" href={FONT_LINK} />
      <div style={lightWashStyle} />

      {!mobile && (
        <aside style={{
          width: sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED, background: SURFACE, display: "flex", flexDirection: "column",
          height: "100vh", flexShrink: 0, zIndex: 99, boxShadow: `1px 0 0 ${BORDER}, 4px 0 16px -12px ${SHADOW_INK}`,
          transition: "width .18s ease", position: "relative",
        }}>
          <div style={{ padding: "18px 16px 14px", display: "flex", alignItems: "center", justifyContent: sidebarOpen ? "space-between" : "center", flexShrink: 0 }}>
            {sidebarOpen && (
              <button onClick={() => go("home")} aria-label="Home" style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                <img src={logo} alt="Ebuos" style={{ height: 24, width: "auto" }} />
              </button>
            )}
            <CollapseToggle open={sidebarOpen} onClick={() => setSidebarOpen((v) => !v)} />
          </div>

          {sidebarOpen && (
            <div style={{ padding: "0 16px 14px", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 16, background: SURFACE_SOFT, boxShadow: `inset 0 0 0 1.5px ${BORDER_STRONG}`, cursor: "text" }}>
                <Search size={14} color={TEXT_FAINT} />
                <span style={{ fontSize: 12.5, color: TEXT_FAINT, fontWeight: 600 }}>Search…</span>
                <span style={mono({ color: TEXT_FAINT, marginLeft: "auto", background: SURFACE, padding: "2px 6px", borderRadius: 6, boxShadow: `inset 0 0 0 1px ${BORDER_STRONG}` })}>⌘K</span>
              </div>
            </div>
          )}

          <nav style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "0 10px", scrollbarWidth: "thin", overscrollBehavior: "contain" } as React.CSSProperties}>
            {NAV.map((item) => (
              <NavButton
                key={item.id} icon={item.icon} label={item.label} hint={item.hint}
                active={view === item.id} collapsed={!sidebarOpen} onClick={() => go(item.id)}
              />
            ))}
          </nav>

          {/* ═══ BUSINESS SWITCHER + MODE TOGGLE ═══
              Click the business row to open a popover of every merchant this
              user owns; picking one just swaps merchantId (or sends you to
              the test-mode route, if that merchant is in test mode). The
              oval toggle underneath is a read-only indicator of the CURRENT
              merchant's mode — clicking "Test" while live jumps to test mode
              for this merchant; "Live" is inert since you're already here. */}
          <div ref={switcherRef} style={{ padding: sidebarOpen ? "14px 16px" : "14px 8px", boxShadow: `inset 0 1px 0 ${BORDER}`, flexShrink: 0, position: "relative" }}>
            <button
              onClick={() => setSwitcherOpen((v) => !v)}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", justifyContent: sidebarOpen ? "flex-start" : "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              title="Switch business"
            >
              <Bead size={36} tone="mark">{initial}</Bead>
              {sidebarOpen && (
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={{ ...trunc, fontSize: 12.5, fontWeight: 600, fontFamily: "'Fredoka',sans-serif" }}>
                    {merchant?.businessName || "Your business"}
                  </div>
                  <div style={{ ...trunc, fontSize: 10.5, color: TEXT_FAINT }}>{merchant?.email || ""}</div>
                </div>
              )}
              {sidebarOpen && merchants.length > 1 && (
                <ChevronRight
                  size={13} strokeWidth={2.6}
                  style={{ color: TEXT_FAINT, transform: switcherOpen ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }}
                />
              )}
            </button>

            {sidebarOpen && (
              <div style={{ marginTop: 12 }}>
                <ModeToggle isLive={isLive} onSwitchToTest={onModeClick} />
              </div>
            )}

            <AnimatePresence>
              {switcherOpen && merchants.length > 1 && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} style={switcherPopStyle}>
                  <div style={mono({ padding: "10px 12px 4px", color: TEXT_FAINT })}>Your businesses</div>
                  {merchants.map((m: Merchant) => (
                    <SwitcherItem
                      key={m.merchantId} label={m.businessName || "Untitled business"}
                      active={m.merchantId === merchantId} onClick={() => switchMerchant(m)}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </aside>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, position: "relative", zIndex: 1 }}>
        <header ref={popRef} style={{
          height: HEADER_H, flexShrink: 0, background: SURFACE, display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 18px", gap: 12, position: "relative", zIndex: 100, boxShadow: `0 1px 0 ${BORDER}, 0 8px 20px -16px ${SHADOW_INK}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            {mobile && <img src={logo} alt="Ebuos" style={{ height: 22, width: "auto" }} />}
            <h1 style={{ ...heading(16), ...trunc }}>{titles[view] || "Dashboard"}</h1>
            <ModeChip isLive={isLive} onClick={onModeClick} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <Bead size={36} tone="glass" onClick={() => { setGrid(!grid); setSettings(false); }} ariaLabel="Quick access">
                <Grid3X3 size={16} strokeWidth={2.4} />
              </Bead>
              <AnimatePresence>
                {grid && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} style={dropdownStyle(240, mobile ? -40 : 0)}>
                    <div style={{ padding: "12px 14px 6px" }}>
                      <span style={mono({ color: TEXT_FAINT })}>Jump to</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, padding: "0 8px 12px" }}>
                      {NAV.map((q) => (
                        <GridQuickButton key={q.id} icon={q.icon} label={q.label} onClick={() => go(q.id)} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div style={{ position: "relative" }}>
              <Bead size={36} tone="glass" onClick={() => { setSettings(!settings); setGrid(false); }} ariaLabel="Settings">
                <Settings size={16} strokeWidth={2.4} />
              </Bead>
              <AnimatePresence>
                {settings && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} style={dropdownStyle(260, mobile ? -40 : 0)}>
                    {/* ═══ MOBILE: business switcher + mode toggle live here,
                        since there's no sidebar to hold them. ═══ */}
                    {mobile && merchants.length > 1 && (
                      <>
                        <div style={{ padding: "10px 16px 4px" }}>
                          <span style={mono({ color: TEXT_FAINT })}>Switch business</span>
                        </div>
                        <div style={{ maxHeight: 168, overflowY: "auto" }}>
                          {merchants.map((m: Merchant) => (
                            <MenuButton
                              key={m.merchantId} active={m.merchantId === merchantId} onClick={() => switchMerchant(m)}
                              leading={<Bead size={22} tone="mark" style={{ fontSize: 10 }}>{(m.businessName || "?").charAt(0).toUpperCase()}</Bead>}
                            >
                              {m.businessName || "Untitled business"}
                            </MenuButton>
                          ))}
                        </div>
                        <div style={dividerStyle} />
                      </>
                    )}

                    {mobile && (
                      <>
                        <div style={{ padding: "10px 16px 8px" }}>
                          <ModeToggle isLive={isLive} onSwitchToTest={onModeClick} />
                        </div>
                        <div style={dividerStyle} />
                      </>
                    )}

                    {settingsItems.map((s) => (
                      <MenuButton key={s.key} icon={s.icon} onClick={() => { s.run(); setSettings(false); }}>{s.label}</MenuButton>
                    ))}

                    <div style={dividerStyle} />
                    <MenuButton icon={LogOut} danger onClick={() => dispatch(logout())}>Sign out</MenuButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Bead size={36} tone="mark" style={{ marginLeft: 4 }}>{initial}</Bead>
          </div>

          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2.5, background: WORDMARK, opacity: 0.9 }} />
        </header>

        <main ref={scrollRef} style={{
          flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain", padding: mobile ? "18px 16px 110px" : "26px",
        } as React.CSSProperties}>
          {mError && (
            <div style={plateStyle({ boxShadow: `${BEVEL_SM}, 0 0 0 2px ${PINK}, 0 6px 0 ${PINK_DEEP}`, marginBottom: 16, padding: 14, fontSize: 13, color: PINK_DEEP, fontWeight: 600 })}>
              {mError}
            </div>
          )}
          <AnimatePresence mode="wait">
            <motion.div key={view} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}>
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {mobile && (
        <nav style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 120, background: SURFACE,
          boxShadow: `0 -1px 0 ${BORDER}, 0 -8px 20px -16px ${SHADOW_INK}`, paddingBottom: "env(safe-area-inset-bottom)",
          overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch",
        } as React.CSSProperties}>
          <div style={{ display: "flex", width: "max-content", minWidth: "100%" }}>
            {NAV.map((item) => (
              <Tab key={item.id} icon={item.icon} label={item.label} active={view === item.id} onClick={() => go(item.id)} />
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

/* ═══ HOME — no payment data left; just a welcome + quick links.
   Each link card gets exactly one brand color as its bead, so the page
   still reads mostly black-and-white with three deliberate spots of
   color rather than a themed background. ═══ */
function HomePage({ merchant, isLive, onGo }: { merchant: any; isLive: boolean; onGo: (id: string) => void }) {
  const cards: { id: string; l: string; d: string; i: any; tone: "blue" | "violet" | "pink" }[] = [
    { id: "api",      l: "API",      d: "Mint keys and read the endpoint reference.", i: Key,      tone: "blue" },
    { id: "webhooks", l: "Webhooks", d: "Point events at your server and watch deliveries.", i: Webhook, tone: "violet" },
    { id: "support",  l: "Support",  d: "Open a conversation with the support team.", i: LifeBuoy, tone: "pink" },
  ];

  return (
    <div>
      <div style={heroSlabStyle}>
        <Sparkles size={18} strokeWidth={2.4} style={{ position: "absolute", top: 20, right: 22, color: "rgba(255,255,255,.55)" }} />
        <p style={mono({ color: "rgba(255,255,255,.78)" })}>
          {greeting()}, {merchant?.businessName || "there"}
        </p>
        <h2 style={heading("clamp(22px,3.2vw,30px)", { marginTop: 10, color: WHITE })}>
          Welcome back to your dashboard
        </h2>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,.85)", marginTop: 8 }}>
          {isLive ? "This account is live." : "You're in test mode."}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 14, marginTop: 18 }}>
        {cards.map((c, i) => (
          <LinkCard key={c.id} icon={c.i} title={c.l} description={c.d} tone={c.tone} delay={i * 0.06} onClick={() => onGo(c.id)} />
        ))}
      </div>
    </div>
  );
}